// App routing — pred prihlasenim Login, po prihlaseni Home.

import { useEffect } from "react";
import {
  IonApp,
  IonContent,
  IonPage,
  IonSpinner,
} from "@ionic/react";
import { IonReactRouter } from "@ionic/react-router";
import { Redirect, Route, Switch, useLocation } from "react-router-dom";
import { QueryClientProvider } from "@tanstack/react-query";
import { queryClient } from "./lib/queryClient";
import { useAuthStore } from "./store/authStore";
import { useSyncStore } from "./store/syncStore";
import { useShiftStore } from "./store/shiftStore";
import { useStornoAckStore } from "./store/stornoAckStore";
import { useStopProgressStore } from "./store/stopProgressStore";
import { useWorkspaceStore } from "./store/workspaceStore";
import { WORKSPACE_BASENAME } from "./lib/workspace";
import { LAST_PATH_KEY } from "./lib/storageKeys";
import { LoginScreen } from "./screens/LoginScreen";
import { WorkspaceJoinScreen } from "./screens/WorkspaceJoinScreen";
import { HomeScreen } from "./screens/HomeScreen";
import { ShiftStartScreen } from "./screens/ShiftStartScreen";
import { TodayRouteScreen } from "./screens/TodayRouteScreen";
import { StopDetailScreen } from "./screens/StopDetailScreen";
import { EndShiftScreen } from "./screens/EndShiftScreen";
import { FuelingScreen } from "./screens/FuelingScreen";
import { MaterialScreen } from "./screens/MaterialScreen";
import { CollectionStopScreen } from "./screens/CollectionStopScreen";
import { TrankMaterialScreen } from "./screens/TrankMaterialScreen";
import { DepotScreen } from "./screens/DepotScreen";
import { LamaKlubScreen } from "./screens/LamaKlubScreen";
import { ErrorBoundary } from "./components/ErrorBoundary";

// Re-export kvůli stávajícím importům z obrazovek (definice viz lib/storageKeys).
export { LAST_PATH_KEY } from "./lib/storageKeys";

function AppContent() {
  const { driver, initialized: authInitialized, init: authInit } = useAuthStore();
  const { workspaceId, initialized: wsInitialized, init: wsInit } = useWorkspaceStore();
  const syncInit = useSyncStore((s) => s.init);
  const hydrateStornoAck = useStornoAckStore((s) => s.hydrate);
  const location = useLocation();

  useEffect(() => {
    void wsInit();
  }, [wsInit]);

  useEffect(() => {
    void hydrateStornoAck();
  }, [hydrateStornoAck]);

  useEffect(() => {
    void authInit();
  }, [authInit]);

  useEffect(() => {
    let cleanup: (() => void) | undefined;
    void syncInit().then((c) => (cleanup = c));
    return () => cleanup?.();
  }, [syncInit]);

  // Ukládej aktuální cestu VČETNĚ query (?rid=) — obnoví se při restartu app.
  // Bez query by se u řidiče s víc trasami obnova vrátila na routes[0] (špatná trasa).
  useEffect(() => {
    if (location.pathname !== "/" && !location.pathname.startsWith("/login")) {
      localStorage.setItem(LAST_PATH_KEY, location.pathname + location.search);
    }
  }, [location.pathname, location.search]);

  // Při návratu z navigační app (visibilitychange) obnov stav směny + zastávek.
  // Cooldown 3 min — řidiči přepínají apps na každé zastávce, bez throttle by
  // hydrateFromBackend fetchoval celý event log desítkykrát za hodinu.
  useEffect(() => {
    let lastHydrateAt = 0;
    const HYDRATE_COOLDOWN_MS = 3 * 60_000;
    const handleVisible = () => {
      if (document.visibilityState !== "visible") return;
      const { refresh } = useShiftStore.getState();
      void refresh().then(() => {
        const shift = useShiftStore.getState().shift;
        if (!shift?.id || shift.end_ts) return;
        const now = Date.now();
        if (now - lastHydrateAt < HYDRATE_COOLDOWN_MS) return;
        lastHydrateAt = now;
        void useStopProgressStore.getState().hydrateFromBackend(shift.id);
      });
    };
    document.addEventListener("visibilitychange", handleVisible);
    return () => document.removeEventListener("visibilitychange", handleVisible);
  }, []);

  if (!wsInitialized || !authInitialized) {
    return (
      <IonPage>
        <IonContent className="ion-padding">
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
            }}
          >
            <IonSpinner name="crescent" />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  if (!workspaceId) {
    return <WorkspaceJoinScreen />;
  }

  if (!driver) {
    return <LoginScreen />;
  }

  return (
    <Switch>
      <Route exact path="/" component={HomeScreen} />
      <Route exact path="/shift/start" component={ShiftStartScreen} />
      <Route exact path="/route" component={TodayRouteScreen} />
      <Route exact path="/route/stop/:idx" component={StopDetailScreen} />
      <Route exact path="/shift/end" component={EndShiftScreen} />
      <Route exact path="/shift/fueling" component={FuelingScreen} />
      <Route exact path="/shift/material" component={MaterialScreen} />
      <Route exact path="/route/collection/:id" component={CollectionStopScreen} />
      {/* Materiálové úkoly TRANKu (naložit v Brně / vyložit na cílovém depu).
          Rozlišuje je `?kind=nalozit|vylozit`. */}
      <Route exact path="/route/trank-material/:id" component={TrankMaterialScreen} />
      <Route exact path="/route/depot" component={DepotScreen} />
      <Route exact path="/lamy" component={LamaKlubScreen} />
      <Redirect to="/" />
    </Switch>
  );
}

export default function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <IonApp>
          <IonReactRouter basename={WORKSPACE_BASENAME}>
            <AppContent />
          </IonReactRouter>
        </IonApp>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}
